import {
  Avatar,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { FaTrashCan } from "react-icons/fa6";
import { CRIMSON_RED } from "../../enums/Colors";
import { Link } from "react-router-dom";
import usePrimaryColor from "../../hooks/usePrimaryColor";
import { ThemeContext } from "../../contexts/ThemeContext";
import usePetAvatar from "../../hooks/usePetAvatar";

export function getFirstLetter(string) {
  return String(string).charAt(0);
}

const sideSize = 125;
const avatarSize = 70;

export default function EntityCard({
  data,
  deletable = false,
  link,
  showNotification = false,
  color,
  testId,
}) {
  const { t } = useTranslation();
  // Inactive pets (only listed for master) keep their orange color even if deceased
  const isDeceased = data.deceased && data.active !== false;
  const { mode } = React.useContext(ThemeContext);
  const showIcon = data?.icon;
  const primaryColor = usePrimaryColor();
  const bgColor = color ? color : primaryColor;
  const themeColor = mode == "dark" ? "black" : "white";
  const { avatar, isLoading, error } = usePetAvatar(data.id);

  return (
    <Card
      data-testid={testId}
      sx={{
        width: sideSize,
        borderRadius: 2,
        boxShadow: 3,
        position: "relative",
        maxHeight: "155px",
        border: `2px solid ${isDeceased ? "gray" : bgColor}`,
        background: `linear-gradient(180deg, transparent 50%, ${
          isDeceased ? "gray" : bgColor
        } 50%)`,
        backgroundSize: "200% 200%",
        transition:
          "background-position 250ms ease-in-out, transform 150ms ease-in-out, color 150ms ease-in-out",
        "&:hover": {
          boxShadow: 6,
          border: "none",
          transform: "scale(1.05)",
          backgroundPosition: "100% 100%",
          "& .card-text": { color: isDeceased ? "white" : themeColor }, // Text turns white on hover
          "& .card-actions": { display: "flex" },
        },
      }}
    >
      {/* Notification dot with pulsing animation */}
      {showNotification && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            width: 10,
            height: 10,
            bgcolor: "red",
            borderRadius: "50%",
          }}
        />
      )}

      <Link to={link}>
        <Box display="flex" justifyContent="center" alignItems="center" pt={2}>
          <Avatar
            src={avatar}
            data-testid={testId ? `${testId}.avatar` : undefined}
            slotProps={{
              img: {
                sx: {
                  filter: isDeceased
                    ? "grayscale(100%) brightness(0.8) contrast(0.95)"
                    : "none",
                },
              },
            }}
            sx={{
              width: avatarSize,
              height: avatarSize,
              bgcolor: isDeceased ? "gray" : bgColor,
            }}
          >
            {showIcon ? (
              <i
                className={`fas ${showIcon}`}
                style={{
                  fontSize: 35,
                }}
              />
            ) : (
              <Typography
                variant="h4"
                textAlign={"center"}
                fontWeight={"bold"}
                sx={{
                  overflowWrap: "break-word", // Ensures words break
                  wordBreak: "break-word", // Break long words
                  textOverflow: "ellipsis", // Truncate text with ellipsis if it's too long
                }}
              >
                {String(getFirstLetter(data?.title)).toUpperCase()}
              </Typography>
            )}
          </Avatar>
        </Box>

        <CardContent
          sx={{
            textAlign: "center",
            overflowWrap: "break-word", // Ensures words break
            wordBreak: "break-word", // Break long words
            textOverflow: "ellipsis",
          }}
        >
          <Typography
            fontWeight={"bold"}
            color={isDeceased ? "gray" : bgColor}
            className="card-text"
          >
            {String(data?.title).toUpperCase()}
          </Typography>
        </CardContent>
      </Link>

      {deletable && (
        <CardActions
          className="card-actions"
          sx={{
            position: "absolute",
            top: 1,
            right: 1,
            display: "none",
          }}
        >
          <IconButton sx={{ color: CRIMSON_RED }} title={t("Delete")}>
            <FaTrashCan size={20} />
          </IconButton>
        </CardActions>
      )}
    </Card>
  );
}
